/* BEATSEEK — invite friends/follows into a chat room. */
import React, { useEffect, useState } from 'react';
import { X, Send, Search, Link2, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatInviteModal = ({ room, user, onClose }) => {
  const [candidates, setCandidates] = useState([]);
  const [picked, setPicked] = useState([]);
  const [q, setQ] = useState('');
  const [sent, setSent] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const uid = user?.id || user?.user_id;
    if (!uid) return;
    fetch(`${API}/chat/invite-candidates?user_id=${uid}`)
      .then(r => (r.ok ? r.json() : { users: [] }))
      .then(d => setCandidates(d.users || []))
      .catch(() => {});
  }, [user]);

  const toggle = (id) => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

  const send = async () => {
    if (!picked.length) return;
    try {
      const r = await fetch(`${API}/chat/rooms/${room.id}/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('beatseek_token') || ''}` },
        body: JSON.stringify({ user_ids: picked }),
      });
      const d = await r.json().catch(() => ({}));
      setSent(r.ok ? `Invite sent to ${d.invited || picked.length} BeatSeeker${(d.invited || picked.length) === 1 ? '' : 's'}` : 'Could not send invites');
      if (r.ok) setPicked([]);
    } catch { setSent('Could not send invites'); }
  };

  const inviteLink = `${window.location.origin}/?chat=${room?.id}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
    } catch {
      const t = document.createElement('textarea');
      t.value = inviteLink; document.body.appendChild(t); t.select();
      document.execCommand('copy'); document.body.removeChild(t);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const shown = candidates.filter(c => !q || (c.username || '').toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="fixed inset-0 z-[100020] flex items-center justify-center p-4" data-testid="chat-invite-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-2xl border border-cyan-500/30 bg-gradient-to-b from-slate-900 to-slate-950 p-5 shadow-2xl">
        <button onClick={onClose} data-testid="chat-invite-close" className="absolute right-3 top-3 text-gray-500 hover:text-white"><X size={16} /></button>
        <h3 className="mb-1 text-base font-bold text-white">Invite to {room?.name}</h3>
        <p className="mb-3 text-xs text-gray-400">They'll get a notification with a link straight into this room.</p>

        <div className="mb-3 flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2">
          <Search size={13} className="text-gray-500" />
          <input
            data-testid="chat-invite-search"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search friends and follows"
            className="w-full bg-transparent py-2 text-xs text-white outline-none placeholder:text-gray-600"
          />
        </div>

        <div className="mb-3 max-h-56 space-y-1 overflow-y-auto">
          {shown.length === 0 && <div className="py-6 text-center text-xs text-gray-500">Nobody to invite yet — follow a few BeatSeekers first.</div>}
          {shown.map(c => (
            <button
              key={c.user_id}
              data-testid={`chat-invite-pick-${c.user_id}`}
              onClick={() => toggle(c.user_id)}
              className={`flex w-full items-center gap-2 rounded-lg border px-2 py-1.5 text-left transition-colors ${picked.includes(c.user_id) ? 'border-cyan-400/60 bg-cyan-500/15' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.07]'}`}
            >
              {c.avatar
                ? <img src={c.avatar} alt="" className="h-7 w-7 rounded-full object-cover" />
                : <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-fuchsia-600 text-xs font-bold text-white">{(c.username || '?').charAt(0).toUpperCase()}</span>}
              <span className="flex-1 truncate text-xs font-semibold text-gray-100">{c.username}</span>
              <span className={`h-2 w-2 rounded-full ${c.online ? 'bg-green-400 animate-pulse' : 'bg-gray-600'}`} />
              <span className="text-[9px] uppercase tracking-wide text-gray-500">{c.relation}</span>
            </button>
          ))}
        </div>

        {sent && <div data-testid="chat-invite-status" className="mb-2 text-center text-[11px] font-semibold text-emerald-400">{sent}</div>}

        <div className="mb-3 rounded-lg border border-white/10 bg-white/[0.03] p-2.5">
          <div className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500">Share link — works outside the app</div>
          <div data-testid="chat-invite-link" className="mb-2 truncate rounded bg-black/40 px-2 py-1 text-[10px] text-cyan-300">{inviteLink}</div>
          <button
            data-testid="chat-invite-copy"
            onClick={copyLink}
            className="flex w-full items-center justify-center gap-2 rounded-full border border-cyan-500/40 py-1.5 text-[11px] font-bold text-cyan-300 transition-colors hover:bg-cyan-500/10"
          >
            {copied ? <><Check size={12} /> Link copied</> : <><Link2 size={12} /> Copy invite link</>}
          </button>
        </div>

        <button
          data-testid="chat-invite-send"
          onClick={send}
          disabled={!picked.length}
          className={`flex w-full items-center justify-center gap-2 rounded-full py-2 text-xs font-bold transition-colors ${picked.length ? 'bg-cyan-500 text-slate-900 hover:bg-cyan-400' : 'cursor-not-allowed bg-white/5 text-gray-600'}`}
        >
          <Send size={13} /> Send invite{picked.length > 1 ? `s (${picked.length})` : ''}
        </button>
      </div>
    </div>
  );
};

export default ChatInviteModal;
